import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shell } from '../components/layout/Shell';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, CircleNotch } from '@phosphor-icons/react';

const POLL_MAX = 10;
const POLL_INTERVAL = 2000;

const PaymentSuccess = () => {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const sessionId = sp.get('session_id');
  const [state, setState] = useState('checking'); // checking | paid | failed | timeout
  const [info, setInfo] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState('failed'); return; }
    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setState('paid');
          setInfo(data);
          return;
        }
        if (data.status === 'expired') {
          setState('failed');
          return;
        }
      } catch {
        // keep polling
      }
      if (attemptsRef.current >= POLL_MAX) {
        setState('timeout');
        return;
      }
      setTimeout(poll, POLL_INTERVAL);
    };
    poll();
  }, [sessionId]);

  return (
    <Shell>
      <div className="max-w-[700px] mx-auto px-6 py-24 text-center" data-testid="payment-success">
        {state === 'checking' && (
          <>
            <CircleNotch size={56} className="text-primary animate-spin mx-auto mb-6" />
            <h1 className="font-display font-black text-3xl tracking-tighter mb-2">Confirming payment…</h1>
            <p className="text-sm text-muted-foreground">This usually takes a few seconds.</p>
          </>
        )}
        {state === 'paid' && (
          <>
            <CheckCircle size={56} weight="fill" className="text-primary mx-auto mb-6" />
            <h1 className="font-display font-black text-4xl tracking-tighter mb-3">Welcome to {info?.plan?.toUpperCase()}.</h1>
            <p className="text-muted-foreground mb-8">Your plan is active. Ranked candidates are one click away.</p>
            <div className="flex justify-center gap-2">
              <Button className="rounded-none" onClick={() => nav('/employer')} data-testid="payment-goto-dashboard">Go to dashboard</Button>
              <Button variant="outline" className="rounded-none" onClick={() => nav('/employer/billing')} data-testid="payment-goto-billing">View billing</Button>
            </div>
          </>
        )}
        {state === 'failed' && (
          <>
            <XCircle size={56} weight="fill" className="text-destructive mx-auto mb-6" />
            <h1 className="font-display font-black text-3xl tracking-tighter mb-3">Payment not completed.</h1>
            <Button variant="outline" className="rounded-none" onClick={() => nav('/pricing')} data-testid="payment-retry">Try again</Button>
          </>
        )}
        {state === 'timeout' && (
          <>
            <p className="font-display font-black text-xl mb-3">Still processing…</p>
            <p className="text-sm text-muted-foreground mb-6">Please check your email or the billing page in a minute.</p>
            <Button variant="outline" className="rounded-none" onClick={() => nav('/employer/billing')}>Check billing</Button>
          </>
        )}
      </div>
    </Shell>
  );
};

export default PaymentSuccess;
